/**
 * Decorrelated portfolio.
 *
 * Drawing j grids independently leaves overlaps of 2-3 numbers, which concentrates
 * the stake on one region of the space. Capping the overlap does NOT improve the
 * probability of hitting rank 1 — that stays exactly j / (C(n,k) x C(bmax,bk)).
 * It improves the probability of hitting AT LEAST one intermediate rank for the
 * same money, by covering a wider part of the space.
 */
import type { GameConfig } from './games';
import { drawGrid } from './draw';
import type { DrawOptions, Grid } from './draw';

export const PORTFOLIO_ATTEMPTS = 8000;

export function maxOverlapFor(g: GameConfig): number {
  return Math.max(1, Math.floor(g.k / 3));
}

export interface PortfolioOptions extends DrawOptions {
  maxOverlap?: number;
  attempts?: number;
}

export function drawPortfolio(
  g: GameConfig,
  count = 2,
  options: PortfolioOptions = {},
): Grid[] {
  const maxOverlap = options.maxOverlap ?? maxOverlapFor(g);
  const attempts = options.attempts ?? PORTFOLIO_ATTEMPTS;
  const out: Grid[] = [];

  for (let i = 0; i < attempts && out.length < count; i++) {
    const candidate = drawGrid(g, options);
    const set = new Set(candidate.main);
    const ok = out.every((prev) => {
      let shared = 0;
      for (const x of prev.main) if (set.has(x)) shared++;
      return shared <= maxOverlap;
    });
    if (ok) out.push(candidate);
  }
  // Safety net: relax the constraint rather than fail to produce grids.
  while (out.length < count) out.push(drawGrid(g, options));
  return out;
}

/** Largest number of shared main numbers between any two grids of the portfolio. */
export function largestOverlap(grids: readonly Grid[]): number {
  let max = 0;
  for (let i = 0; i < grids.length; i++) {
    const set = new Set((grids[i] as Grid).main);
    for (let j = i + 1; j < grids.length; j++) {
      let shared = 0;
      for (const x of (grids[j] as Grid).main) if (set.has(x)) shared++;
      if (shared > max) max = shared;
    }
  }
  return max;
}
